import { useEffect } from "react";

// Matches the canonical/og:url host in index.html. Deliberately not derived
// from window.location: dev.embroiderytext.com serves the same build and
// gets excluded from indexing at the Caddy layer (X-Robots-Tag), but its
// canonical/og:url tags should still point at the one URL that's actually
// meant to be indexed, not at itself.
const SITE_ORIGIN = "https://embroiderytext.com";

// Matches the attribute frontend/src/index.ts's renderPage() marks the tags
// it injects server-side with for the same evergreen paths -- this is what
// lets this hook find and clear them on its first mount rather than leaving
// duplicates alongside its own.
const STRUCTURED_DATA_ATTR = "data-structured-data";

interface PageMeta {
  title: string;
  description: string;
  /** Root-relative path, e.g. "/", "/create", "/convert". */
  path: string;
  /**
   * schema.org objects for this page, if any. Define as a module-level
   * constant in the page component (same as PAGE_TITLE/PAGE_DESCRIPTION),
   * not inline in JSX -- an inline array literal gets a new reference every
   * render, which would re-run this effect on every render rather than only
   * when the page actually changes.
   */
  structuredData?: object[];
  /**
   * What a link preview (iMessage, Slack, Twitter, ...) shows, if different
   * from the browser tab title/meta description -- a <title> earns its
   * keep by describing the page for search and the tab bar, but "Check out
   * this embroidery design" is an invitation, not a page description, and
   * forcing one string to do both jobs reads oddly in at least one of them.
   * Falls back to title/description when omitted.
   */
  ogTitle?: string;
  ogDescription?: string;
  /**
   * Absolute URL to a share-preview image, if this page has a specific one
   * (e.g. ReplayPage's design PNG) -- must include the scheme and host, not
   * a root-relative path, since a link-preview fetcher (unlike a browser)
   * has no page to resolve a relative URL against. No baseline <meta> tag
   * exists for this in index.html the way title/description do, since most
   * pages have no image to offer -- the tag is created on mount and removed
   * on unmount rather than swapped in place.
   */
  image?: string;
}

function swapMetaContent(selector: string, content: string): string | null {
  const tag = document.querySelector(selector);
  if (!tag) return null;
  const previous = tag.getAttribute("content");
  tag.setAttribute("content", content);
  return previous;
}

/**
 * Sets this page's <title>, description, canonical link, Open Graph/Twitter
 * tags, and JSON-LD structured data, restoring whatever was there before on
 * unmount.
 *
 * This is a single-page app with one static index.html shared by every
 * route (see frontend/src/index.ts), so there is nothing else that gives a
 * route its own title or lets Google tell /create and /convert apart from
 * the home page -- without this, every route looks identical to a crawler
 * or a shared link.
 *
 * The structured-data tags are marked with a fixed attribute rather than
 * rendered as JSX elements per page, specifically so this hook can find (and
 * clear) whatever frontend/src/index.ts's renderPage() already injected
 * server-side for a crawler's first, pre-render fetch -- unmarked script
 * tags would just leave a second, duplicate copy once this mounts. The same
 * attribute also makes client-side navigation between routes correct: a
 * route with none removes whatever the previous route left rather than
 * leaving it stuck describing the wrong page.
 *
 * One <script> tag per schema.org object, not one tag holding a JSON array
 * of them -- a page with two or more objects (an FAQPage alongside a
 * WebApplication, say) used to get a single tag serializing the whole
 * array. Google Tag Manager's own page scan (gtag.js, loaded on every page
 * for conversion tracking) reads each application/ld+json block assuming
 * it holds exactly one object with its own top-level @context, and threw a
 * real, live TypeError -- undefined is not an object (evaluating
 * 'r["@context"].toLowerCase') -- trying to read that off of the array
 * itself. Caught in PostHog error tracking (issue 01a0934d), 9+ occurrences
 * across 8 real visitors on /, then /create, then (once this shipped)
 * /convert and /preview too, all Safari. One object per tag is what every
 * consumer -- Google's own included -- actually expects.
 */
export function usePageMeta({
  title,
  description,
  path,
  structuredData,
  ogTitle,
  ogDescription,
  image,
}: PageMeta) {
  useEffect(() => {
    const url = `${SITE_ORIGIN}${path}`;
    const previousTitle = document.title;
    document.title = title;

    const previousDescription = swapMetaContent('meta[name="description"]', description);
    const previousOgTitle = swapMetaContent('meta[property="og:title"]', ogTitle ?? title);
    const previousOgDescription = swapMetaContent(
      'meta[property="og:description"]',
      ogDescription ?? description
    );
    const previousOgUrl = swapMetaContent('meta[property="og:url"]', url);
    const previousTwitterTitle = swapMetaContent('meta[name="twitter:title"]', ogTitle ?? title);
    const previousTwitterDescription = swapMetaContent(
      'meta[name="twitter:description"]',
      ogDescription ?? description
    );

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    const previousCanonical = canonicalTag?.getAttribute("href") ?? null;
    canonicalTag?.setAttribute("href", url);

    // No baseline tag for these two, unlike title/description above -- most
    // pages have no image, so there's nothing in index.html to swap. Create
    // one only when this page actually has an image, and only remove it
    // (rather than restore prior content) on unmount, since there is never
    // a "previous" value worth putting back.
    let createdOgImage: Element | null = null;
    let createdTwitterImage: Element | null = null;
    let previousTwitterCard: string | null = null;
    if (image) {
      if (!document.querySelector('meta[property="og:image"]')) {
        const tag = document.createElement("meta");
        tag.setAttribute("property", "og:image");
        document.head.appendChild(tag);
        createdOgImage = tag;
      }
      swapMetaContent('meta[property="og:image"]', image);

      if (!document.querySelector('meta[name="twitter:image"]')) {
        const tag = document.createElement("meta");
        tag.setAttribute("name", "twitter:image");
        document.head.appendChild(tag);
        createdTwitterImage = tag;
      }
      swapMetaContent('meta[name="twitter:image"]', image);

      // summary_large_image is what actually renders the image at any
      // size worth looking at -- the baseline "summary" card in
      // index.html is sized for a page that has no image of its own.
      previousTwitterCard = swapMetaContent('meta[name="twitter:card"]', "summary_large_image");
    }

    // Whatever's already marked gets cleared and rebuilt fresh below --
    // this only ever finds something on the very first mount of the very
    // first page (index.ts's SSR tags, or nothing at all), since a route
    // change always runs the previous page's own cleanup (removing its
    // tags) before this effect runs for the new one.
    document
      .querySelectorAll(`script[${STRUCTURED_DATA_ATTR}]`)
      .forEach((tag) => tag.remove());
    const createdScripts: HTMLScriptElement[] = [];
    for (const item of structuredData ?? []) {
      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.setAttribute(STRUCTURED_DATA_ATTR, "");
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
      createdScripts.push(script);
    }

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null) {
        swapMetaContent('meta[name="description"]', previousDescription);
      }
      if (previousOgTitle !== null) {
        swapMetaContent('meta[property="og:title"]', previousOgTitle);
      }
      if (previousOgDescription !== null) {
        swapMetaContent('meta[property="og:description"]', previousOgDescription);
      }
      if (previousOgUrl !== null) {
        swapMetaContent('meta[property="og:url"]', previousOgUrl);
      }
      if (previousTwitterTitle !== null) {
        swapMetaContent('meta[name="twitter:title"]', previousTwitterTitle);
      }
      if (previousTwitterDescription !== null) {
        swapMetaContent('meta[name="twitter:description"]', previousTwitterDescription);
      }
      if (canonicalTag && previousCanonical !== null) {
        canonicalTag.setAttribute("href", previousCanonical);
      }

      if (createdOgImage) createdOgImage.remove();
      if (createdTwitterImage) createdTwitterImage.remove();
      if (previousTwitterCard !== null) {
        swapMetaContent('meta[name="twitter:card"]', previousTwitterCard);
      }

      createdScripts.forEach((script) => script.remove());
    };
  }, [title, description, path, structuredData, ogTitle, ogDescription, image]);
}
